import ItiButton from "@/iti-core-react/components/ItiButton/ItiButton";
import ItiLoading from "@/iti-core-react/components/ItiLoading/ItiLoading";
import ItiPasswordBoxComponent from "@/iti-core-react/components/ItiPasswordBox/ItiPasswordBox";
import ItiTextBoxComponent from "@/iti-core-react/components/ItiTextBox/ItiTextBox";
import { useLanguage } from "@/iti-core-react/hooks/useLanguage";
import { useThemeConfig } from "@/shared/contexts/ThemeConfigContext";
import ValidationGroup from "devextreme-react/cjs/validation-group";
import { InitializedEvent } from "devextreme/ui/validation_group";
import { Suspense, useEffect, useState } from "react";
import CardRecoveryPassword from "../CardRecoveryPassword/CardRecoveryPassword";
import { useAuth } from "../LoginAuth";
import { getRequiredPassword, getRequiredUsername } from "../LoginForm.utils";
import {
	LegalConditionsPopupLogin,
	legalConditionsPopup,
} from "../Popup/LegalConditionsPopupLogin";
import PoweredByIti from "@/shared/components/PoweredByIti/PoweredByIti.tsx";
import("./CardLogin.scss");

export default function CardLogin() {
	const { t } = useLanguage();
	const { logoLogin, centeredLogin, enterpriseName } = useThemeConfig();
	const [logoLoginError, setLogoLoginError] = useState(false);
	const {
		formData,
		authenticateUser,
		setIsAuthenticated,
		legalConditionsPopupVisible,
		setLegalConditionsPopupVisible,
		user,
		setProfile,
		checkUserStatus,
	} = useAuth();

	// Asegurarse de que se verifique el estado del usuario después de autenticarse
	useEffect(() => {
		if (user) {
			checkUserStatus();
		}
	}, [user, checkUserStatus]);
	const [credentials, setCredentials] = useState<{
		userName: string;
		password: string;
		email: string;
	}>({ userName: "", password: "", email: "" });
	const [validationGroup, setValidationGroup] = useState<any>();
	const requiredUsername = getRequiredUsername(t);
	const requiredPassword = getRequiredPassword(t);
	const [isPasswordRecovery, setIsPasswordRecovery] = useState<boolean>(false);

	function onSubmit(e: any) {
		e?.preventDefault?.();
		submitCredentialsForm(e);
	}

	async function submitCredentialsForm(e: any) {
		if (e?.validationGroup?.validate()?.isValid) {
			formData.current = credentials;
			try {
				const loginSuccess = await authenticateUser();
				if (!loginSuccess) {
					setIsAuthenticated(false);
				} else {
					// Si el login fue exitoso, verificar el estado del usuario para mostrar el popup si es necesario

					await checkUserStatus();
				}
			} catch (error) {
				setIsAuthenticated(false);
			}
		}
		if (e === legalConditionsPopup && user) {
			if (setProfile) {
				setProfile(user);
			}
		}
	}

	function onHideLegalConditionsPopUp() {
		setLegalConditionsPopupVisible(false);
	}

	function onHidePasswordRecoveryPopUp() {
		setIsPasswordRecovery(false);
	}

	function onEnterKey() {
		onSubmit({ validationGroup: validationGroup.instance() });
	}

	function onValidationGroupInitialized(e: InitializedEvent) {
		setValidationGroup(e.component);
	}

	function cardLoginForm() {
		return (
			<form onSubmit={onSubmit}>
				<ValidationGroup onInitialized={onValidationGroupInitialized}>
					<div className="login-container-form">
						<div className="d-flex justify-content-center">
							{(!logoLogin || logoLoginError) && enterpriseName ? (
								<div className="logo-enterprise-name">{enterpriseName}</div>
							) : (
								<img
									src={logoLogin}
									alt="Logo"
									className="logo-app-login"
									onError={() => setLogoLoginError(true)}
								/>
							)}
						</div>
						<div className="col-12">
							<ItiTextBoxComponent
								placeholder={t("login.username")}
								textBoxAttributes={{ class: "login-form-field" }}
								value={credentials.userName}
								onValueChanged={(e: any) =>
									setCredentials((prev: any) => ({
										...prev,
										userName: e.value,
									}))
								}
								validationRules={requiredUsername}
							/>
						</div>
						<div className="col-12">
							<ItiPasswordBoxComponent
								placeholder={t("login.password")}
								elementAttr={{ class: "login-form-field-2" }}
								value={credentials.password}
								onValueChanged={(e: any) =>
									setCredentials((prev: any) => ({
										...prev,
										password: e.value,
									}))
								}
								validationRules={requiredPassword}
								onEnterKey={onEnterKey}
							/>
						</div>
						<div className="col-12 login-form-link-container">
							<a
								href="#"
								className="login-form-link"
								onClick={() => {
									setIsPasswordRecovery(true);
								}}
							>
								{t("login.forgot-password")}
							</a>
						</div>
						<div className="col-12 d-flex justify-content-center">
							<ItiButton
								text={t("login.title")}
								elementAttr={{ class: "login-form-button" }}
								onClick={onSubmit}
								stylingMode="contained"
								type="default"
							/>
						</div>
						<PoweredByIti displayWisephereIcon={centeredLogin} />
					</div>
				</ValidationGroup>
			</form>
		);
	}

	return (
		<>
			<div className="d-flex card-login">
				<div className="row">
					<div className="col-12 d-flex justify-lg-content-end">
						<div className="row justify-content-center">{cardLoginForm()}</div>
					</div>
				</div>
			</div>

			{isPasswordRecovery && (
				<Suspense fallback={<ItiLoading />}>
					<CardRecoveryPassword
						finishPasswordRecovery={onHidePasswordRecoveryPopUp}
					/>
				</Suspense>
			)}

			{legalConditionsPopupVisible && (
				<Suspense fallback={<ItiLoading />}>
					<LegalConditionsPopupLogin
						onSubmit={onSubmit}
						onHideLegalConditionsPopUp={onHideLegalConditionsPopUp}
					/>
				</Suspense>
			)}
		</>
	);
}
